import * as React from "react";
import { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Tooltip, IconButton, Typography, Box } from "@mui/material";
import { CreateNewFolderOutlined, FolderOutlined } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DriveService from "../../services/DriveService";
import useDrive from "../../hooks/useDrive";
import useNotification from "../../hooks/useNotification";
import { DRIVE_TYPES } from "../../config/appConst";

const SMILEFY_SUBFOLDERS = ["1. Photos", "2. IO Scans", "3. CBCT", "4. XRays", "5. Video", "6. Other"];

const optionSx = (selected) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 1.5,
  p: 2,
  borderRadius: 2,
  cursor: "pointer",
  border: `1px solid ${selected ? "#7c6af7" : "var(--border)"}`,
  bgcolor: selected ? "rgba(124,106,247,0.12)" : "var(--bg-input)",
  transition: "all 0.15s",
  "&:hover": {
    borderColor: "#7c6af7",
    bgcolor: "rgba(124,106,247,0.08)",
  },
});

export default function CreateNewFolderForm({ parent }) {
  const { addNotification } = useNotification();
  const { changeCurrentDrive, CurrentDrive } = useDrive();
  const [open, setOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [type, setType] = useState("empty"); // 'empty' | 'smilefy'

  const handleClose = () => { setOpen(false); setFolderName(""); setType("empty"); };

  const handleCreate = async () => {
    if (!folderName.trim()) return;
    const { driveType } = CurrentDrive;
    const basePath = parent && parent !== "" && parent !== "/"
      ? `${parent}/${folderName}` : folderName;

    // Create root folder
    await DriveService.createFolder({ folder: basePath }, driveType);

    // Create subfolders if SmileFy Structure
    if (type === "smilefy") {
      for (const sub of SMILEFY_SUBFOLDERS) {
        await DriveService.createFolder({ folder: `${basePath}/${sub}` }, driveType);
      }
    }

    const drive = await DriveService.getFileTree(parent, driveType);
    if (driveType === DRIVE_TYPES.SHARED)
      changeCurrentDrive({ parent, drive: {}, sharedDrive: drive, driveType });
    else
      changeCurrentDrive({ parent, drive, sharedDrive: {}, driveType });

    addNotification({ type: "success", message: type === "smilefy" ? "SmileFy folder structure created" : "Folder created" });
    handleClose();
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      color: "var(--text-1)", borderRadius: 2,
      bgcolor: "var(--bg-input)",
      "& fieldset": { borderColor: "var(--border)" },
      "&:hover fieldset": { borderColor: "var(--border-h)" },
      "&.Mui-focused fieldset": { borderColor: "#7c6af7", borderWidth: 1 },
    },
    "& .MuiInputBase-input::placeholder": { color: "var(--text-3)", opacity: 1 },
  };

  return (
    <>
      <Tooltip title="New folder" arrow>
        <Button
          variant="outlined" size="small"
          startIcon={<CreateNewFolderOutlined sx={{ fontSize: 16 }} />}
          onClick={() => setOpen(true)}
          sx={{
            height: 36, fontSize: "13px", fontWeight: 600, textTransform: "none",
            borderRadius: 2, px: 2,
            color: "var(--text-2)", borderColor: "var(--border)",
            bgcolor: "transparent",
            "&:hover": { borderColor: "var(--text-1)", color: "var(--text-1)", bgcolor: "var(--border)" },
          }}
        >
          New Folder
        </Button>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "var(--bg-card)", border: "1px solid var(--border)",
            borderRadius: 3, boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
          }
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
          <CreateNewFolderOutlined sx={{ color: "var(--text-1)", fontSize: 20 }} />
          <Typography sx={{ color: "var(--text-1)", fontWeight: 600, fontSize: 16, flex: 1 }}>
            New Folder
          </Typography>
          <IconButton size="small" onClick={handleClose}
            sx={{ color: "var(--text-3)", "&:hover": { color: "var(--text-1)" } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1, pb: 2 }}>
          {/* Folder name */}
          <TextField
            autoFocus fullWidth
            placeholder="Folder name"
            value={folderName}
            onChange={e => setFolderName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleCreate()}
            variant="outlined"
            sx={{ mt: 1, mb: 2.5, ...inputSx }}
          />

          {/* Type selector */}
          <Typography sx={{ color: "var(--text-3)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", mb: 1.5 }}>
            Folder Type
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            {/* Empty */}
            <Box sx={optionSx(type === "empty")} onClick={() => setType("empty")}>
              <FolderOutlined sx={{ fontSize: 36, color: type === "empty" ? "#7c6af7" : "var(--text-3)" }} />
              <Typography sx={{ color: type === "empty" ? "var(--text-1)" : "var(--text-2)", fontWeight: 600, fontSize: 13 }}>
                Empty
              </Typography>
            </Box>

            {/* SmileFy Structure */}
            <Box sx={optionSx(type === "smilefy")} onClick={() => setType("smilefy")}>
              <AutoAwesomeIcon sx={{ fontSize: 36, color: type === "smilefy" ? "#e85878" : "var(--text-3)" }} />
              <Typography sx={{ color: type === "smilefy" ? "var(--text-1)" : "var(--text-2)", fontWeight: 600, fontSize: 13 }}>
                SmileFy Structure
              </Typography>
            </Box>
          </Box>

          {/* SmileFy subfolder preview */}
          {type === "smilefy" && (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: "rgba(232,88,120,0.06)", border: "1px solid rgba(232,88,120,0.15)" }}>
              <Typography sx={{ color: "var(--text-3)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
                Subfolders
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {SMILEFY_SUBFOLDERS.map(sub => (
                  <Box key={sub} sx={{
                    display: "flex", alignItems: "center", gap: 0.5,
                    px: 1.25, py: 0.4, borderRadius: 1.5,
                    bgcolor: "var(--border)", border: "1px solid var(--border-h)",
                  }}>
                    <FolderOutlined sx={{ fontSize: 13, color: "#e85878" }} />
                    <Typography sx={{ color: "var(--text-2)", fontSize: 12, fontWeight: 500 }}>
                      {sub}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={handleClose}
            sx={{ color: "var(--text-3)", textTransform: "none", fontWeight: 500, "&:hover": { color: "var(--text-1)", bgcolor: "var(--border)" } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!folderName.trim()}
            sx={{
              textTransform: "none", fontWeight: 600, px: 3, borderRadius: 2,
              bgcolor: "#7c6af7", color: "#fff",
              "&:hover": { bgcolor: "#6a58e0" },
              "&.Mui-disabled": { bgcolor: "var(--border)", color: "var(--text-3)" },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
